import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { LuCheck, LuCircle } from 'react-icons/lu';

export interface PasswordRule {
  key: string;
  label: string;
  passed: boolean;
}

const MIN_LENGTH = 8;

export function evaluatePassword(
  password: string,
  t: TFunction<'auth'>,
): {
  rules: PasswordRule[];
  allPassed: boolean;
} {
  const rules: PasswordRule[] = [
    {
      key: 'length',
      label: t('rules.min_length', { count: MIN_LENGTH }),
      passed: password.length >= MIN_LENGTH,
    },
    { key: 'upper', label: t('rules.uppercase'), passed: /[A-Z]/.test(password) },
    { key: 'lower', label: t('rules.lowercase'), passed: /[a-z]/.test(password) },
    { key: 'number', label: t('rules.number'), passed: /\d/.test(password) },
    { key: 'symbol', label: t('rules.symbol'), passed: /[^A-Za-z0-9]/.test(password) },
  ];
  return { rules, allPassed: rules.every((r) => r.passed) };
}

export interface PasswordRulesProps {
  password: string;
}

export default function PasswordRules({ password }: PasswordRulesProps) {
  const { t } = useTranslation('auth');
  const { rules } = evaluatePassword(password, t);

  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--nrg-space-1) var(--nrg-space-3)',
        fontSize: 'var(--nrg-font-size-body-sm)',
      }}
    >
      {rules.map((rule) => (
        <li
          key={rule.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--nrg-space-2)',
            color: rule.passed ? 'var(--nrg-color-healthy)' : 'var(--nrg-color-text-muted)',
            transition: 'color var(--nrg-duration-fast) var(--nrg-ease-standard)',
          }}
        >
          {rule.passed ? <LuCheck size={14} aria-hidden /> : <LuCircle size={14} aria-hidden />}
          <span>{rule.label}</span>
        </li>
      ))}
    </ul>
  );
}
